import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import FormField, { inputStyle } from '../components/FormField';
import Button from '../components/Button';

// A deliberate, not-random pattern: a diagonal run plus a small cluster,
// like a glaze test sheet — a handful of fired tiles among the unfired rest.
const GLAZE_TEAL = new Set([0, 9, 18, 27, 36, 5, 6, 13, 14]);
const GLAZE_WARM = new Set([44]);

function TileHero() {
  const tiles = Array.from({ length: 48 });
  return (
    <div className="tile-hero" aria-hidden="true">
      {tiles.map((_, i) => (
        <div
          key={i}
          className={`tile${GLAZE_TEAL.has(i) ? ' tile-glaze' : GLAZE_WARM.has(i) ? ' tile-warm' : ''}`}
          style={{ animationDelay: `${(i % 8) * 0.15 + Math.floor(i / 8) * 0.1}s` }}
        />
      ))}
    </div>
  );
}

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const from = (location.state && location.state.from) || '/';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      // A NETWORK_ERROR here usually means the request never reached the API —
      // most often the backend isn't running, or the browser address doesn't
      // match the backend's CORS_ALLOWED_ORIGINS. Say so, instead of a vague
      // "unable to log in", which is what made this look like "nothing happens".
      if (err.code === 'NETWORK_ERROR') {
        setError(
          "Couldn't reach the server. Check that the backend is running, and that you opened this app at the same address configured in the backend's CORS_ALLOWED_ORIGINS (e.g. http://localhost:3000).",
        );
      } else {
        setError(err.message || 'Unable to log in. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex' }}>
      <div
        style={{
          flex: '1 1 50%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          background: 'var(--color-structural)',
          padding: 'var(--space-7) var(--space-6)',
        }}
        className="login-hero-panel"
      >
        <div style={{ maxWidth: 420, margin: '0 auto', width: '100%' }}>
          <TileHero />
          <div style={{ marginTop: 'var(--space-6)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 'var(--space-3)' }}>
              <span className="tile-mark" aria-hidden="true" />
              <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--color-structural-text-muted)' }}>
                Dispatch Analysis
              </span>
            </div>
            <h1 style={{ fontSize: 'var(--font-size-xl)', color: 'var(--color-structural-text)', marginBottom: 'var(--space-2)' }}>
              Know exactly what's shipped, what's pending, and what's next.
            </h1>
            <p style={{ color: 'var(--color-structural-text-muted)', margin: 0, lineHeight: 1.6 }}>
              Stock, orders, dispatch, and production in one place — with AI forecasting,
              classification, and recommendations built on top of your own real data.
            </p>
          </div>
        </div>
      </div>

      <div
        style={{
          flex: '1 1 50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 'var(--space-5)',
          background: 'var(--color-bg)',
        }}
      >
        <div style={{ width: '100%', maxWidth: 380 }}>
          <div style={{ marginBottom: 'var(--space-5)' }}>
            <h2 style={{ fontSize: 'var(--font-size-lg)', marginBottom: 4 }}>Sign in</h2>
            <p style={{ color: 'var(--color-text-secondary)', margin: 0, fontSize: 'var(--font-size-sm)' }}>
              Enter your credentials to access your dashboard.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="card" style={{ marginBottom: 0 }}>
            {error && (
              <div
                role="alert"
                style={{
                  background: 'var(--color-accent-warm-soft)',
                  border: '1px solid var(--color-danger)',
                  color: 'var(--color-danger)',
                  borderRadius: 'var(--radius-sm)',
                  padding: 'var(--space-3)',
                  fontSize: 'var(--font-size-sm)',
                  marginBottom: 'var(--space-4)',
                }}
              >
                {error}
              </div>
            )}

            <FormField label="Email address">
              <input
                type="email"
                required
                autoComplete="username"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={inputStyle}
              />
            </FormField>

            <FormField label="Password">
              <input
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={inputStyle}
              />
            </FormField>

            <Button
              type="submit"
              disabled={submitting}
              style={{ width: '100%', marginTop: 'var(--space-2)' }}
            >
              {submitting ? 'Signing in…' : 'Sign in'}
            </Button>

            <p style={{ textAlign: 'center', fontSize: 'var(--font-size-sm)', marginTop: 'var(--space-3)', marginBottom: 0 }}>
              <Link to="/forgot-password" style={{ color: 'var(--color-accent)' }}>Forgot password?</Link>
            </p>
          </form>

          <p style={{ textAlign: 'center', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 'var(--space-5)' }}>
            Access is managed by your company administrator.
          </p>
        </div>
      </div>
    </div>
  );
}
